import { lazy, Suspense, useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from 'react';
import { MODEL } from '../shared/generation';
import { countLines } from './code';
import { EXAMPLES } from './examples';
import { Rail } from './Rail';
import type { Replay } from './recording';
import { OPENING_REPLAY, REPLAY_BY_CHIP, REPLAYS } from './replays';
import { codeAt, initialState, isBusy, reducer, type Generation, type Run, type RunFailure } from './run';
import type { BundlerSilenceReport, CodeMeta, CodeView, CompileErrorReport, CompileReport, Pane, StageIntent } from './Sandbox';
import { Scrubber } from './Scrubber';
import { readEventStream } from './sse';
import { parseStreamEvent, StreamProtocolError, StreamSequence } from './stream';
import { TurnstileWidget } from './Turnstile';
import { resetTurnstile, turnstileEnabled } from './turnstile-client';
import { WorkspaceBoundary } from './WorkspaceBoundary';

/** Mirrors MAX_PROMPT_CHARS in functions/api/generate.ts. */
const MAX_PROMPT_CHARS = 2000;

const CASE_STUDY_URL = 'https://rokass.org/work/ai-ui-generator/';
const PRIVACY_URL = 'https://rokass.org/en/privacy/';

let sandboxChunk: ReturnType<typeof importSandbox> | null = null;

function importSandbox() {
  return import('./Sandbox');
}

function loadSandbox() {
  sandboxChunk ??= importSandbox();
  return sandboxChunk;
}

const Sandbox = lazy(() => loadSandbox().then((m) => ({ default: m.Sandbox })));

function prefetchSandbox() {
  loadSandbox().catch(() => undefined);
}

function isErrorBody(body: unknown): body is { error: string; code?: string } {
  if (typeof body !== 'object' || body === null) return false;
  const { error, code } = body as { error?: unknown; code?: unknown };
  return typeof error === 'string' && error !== '' && (code === undefined || typeof code === 'string');
}

async function describeFailure(res: Response): Promise<RunFailure> {
  const text = await res.text();
  const body = ((): unknown => {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  })();
  if (isErrorBody(body)) {
    return {
      label: 'REQUEST FAILED',
      code: body.code === undefined ? `HTTP ${res.status}` : `${res.status} ${body.code}`,
      message: body.code === undefined ? body.error : `${body.error} (${body.code})`,
    };
  }
  return { label: 'REQUEST FAILED', code: `HTTP ${res.status}`, message: `Request failed (${res.status})` };
}

const recordedDate = (iso: string) => iso.slice(0, 10);

export default function App() {
  const [state, dispatch] = useReducer(reducer, OPENING_REPLAY, initialState);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [pane, setPane] = useState<Pane>('preview');
  const runSeq = useRef(0);
  const playback = useRef<number | null>(null);

  const run = state.run;
  const busy = isBusy(run);

  useEffect(
    () => () => {
      if (playback.current !== null) window.clearTimeout(playback.current);
    },
    [],
  );

  const generate = useCallback(
    async (prompt: string) => {
      if (!prompt.trim() || busy) return;
      if (turnstileEnabled && turnstileToken === null) {
        dispatch({ type: 'blocked', error: 'Waiting on the verification check — try again in a moment.' });
        return;
      }
      const id = ++runSeq.current;
      const startedAt = performance.now();
      const at = () => performance.now() - startedAt;
      dispatch({ type: 'start', id, prompt, replay: null, startedAt });
      let stage: 'request' | 'stream' = 'request';
      try {
        let res: Response;
        try {
          res = await fetch('/api/generate', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(turnstileToken === null ? { prompt } : { prompt, turnstileToken }),
          });
        } catch (e) {
          dispatch({ type: 'fail', id, at: at(), failure: { label: 'REQUEST FAILED', code: 'network', message: e instanceof Error ? e.message : String(e) } });
          return;
        }
        if (!res.ok) {
          dispatch({ type: 'fail', id, at: at(), failure: await describeFailure(res) });
          return;
        }
        stage = 'stream';
        dispatch({ type: 'responded', id, at: at() });
        if (!res.body) throw new StreamProtocolError('The server returned no response body.');

        const sequence = new StreamSequence();
        for await (const data of readEventStream(res.body)) {
          const event = parseStreamEvent(data);
          sequence.accept(event);
          dispatch({ type: 'event', id, at: at(), event });
          if (sequence.ended) return;
        }
        dispatch({
          type: 'fail',
          id,
          at: at(),
          failure: { label: 'STREAM FAILED', code: 'truncated', message: 'The response ended before the component was complete.' },
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        const code = e instanceof StreamProtocolError ? 'protocol' : 'network';
        dispatch({ type: 'fail', id, at: at(), failure: { label: stage === 'request' ? 'REQUEST FAILED' : 'STREAM FAILED', code, message } });
      } finally {
        if (turnstileEnabled) {
          setTurnstileToken(null);
          resetTurnstile();
        }
      }
    },
    [busy, turnstileToken],
  );

  const replay = useCallback(
    (recording: Replay) => {
      if (busy) return;
      const id = ++runSeq.current;
      const startedAt = performance.now();
      dispatch({ type: 'start', id, prompt: recording.prompt, replay: recording, startedAt });
      let next = 0;
      const step = () => {
        const elapsed = performance.now() - startedAt;
        while (next < recording.events.length && recording.events[next].t <= elapsed) {
          dispatch({ type: 'event', id, at: elapsed, event: recording.events[next].event });
          next++;
        }
        playback.current = next < recording.events.length ? window.setTimeout(step, recording.events[next].t - elapsed) : null;
      };
      step();
    },
    [busy],
  );

  const runPrompt = useCallback(
    (prompt: string) => {
      dispatch({ type: 'setPrompt', prompt });
      generate(prompt);
    },
    [generate],
  );

  const onCompiled = useCallback((report: CompileReport) => {
    dispatch({ type: 'compiled', ...report, at: performance.now() });
  }, []);

  const onCompileError = useCallback((report: CompileErrorReport) => {
    if (!report.swapped) dispatch({ type: 'compileError', code: report.code, error: report.error });
  }, []);

  const onBundlerSilent = useCallback((report: BundlerSilenceReport) => {
    dispatch({ type: 'bundlerSilent', ...report, at: performance.now() });
  }, []);

  const onBundlerRetry = useCallback(() => dispatch({ type: 'bundlerRetry' }), []);

  const onScrub = useCallback((t: number | null) => dispatch({ type: 'scrub', t }), []);
  const showCode = useCallback(() => setPane('code'), []);

  const workspace = run !== null && (busy || state.generation !== null || run.phase === 'incomplete');
  const scrubbable =
    workspace &&
    run !== null &&
    run.texts.length > 0 &&
    (run.phase === 'rendered' || run.phase === 'render-failed' || run.phase === 'incomplete');
  const rewound = scrubbable && state.scrub !== null ? state.scrub : null;
  const streamedLines = run === null ? 0 : countLines(run.code);

  let code: CodeView = { mode: 'edit' };
  let codeMeta: CodeMeta = { kind: 'final', partial: false };
  let stage: StageIntent = { kind: 'live' };
  if (run !== null && busy) {
    code = { mode: 'view', code: run.code, caret: 'live', cut: false, follow: true };
    codeMeta = run.texts.length === 0 ? { kind: 'waiting' } : { kind: 'streaming', lines: streamedLines };
    stage = { kind: 'hold', caption: 'Renders when the component is complete' };
  } else if (run !== null && rewound !== null) {
    const partial = codeAt(run, rewound);
    const beforeEnd = run.streamEndedAt !== null && rewound < run.streamEndedAt;
    code = { mode: 'view', code: partial, caret: beforeEnd ? 'rewind' : null, cut: false, follow: true };
    codeMeta = { kind: 'rewound', lines: countLines(partial), total: streamedLines };
    stage = { kind: 'rewind', at: rewound, prior: run.prior };
  } else if (run !== null && run.phase === 'incomplete') {
    code = { mode: 'view', code: run.code, caret: null, cut: true, follow: true };
    codeMeta = { kind: 'final', partial: true };
    stage = { kind: 'incomplete', lines: streamedLines, outputTokens: run.final?.output_tokens ?? null };
  } else if (run !== null && run.phase === 'compiling') {
    stage = { kind: 'hold', caption: 'Compiling' };
  } else if (run !== null && (run.phase === 'request-failed' || run.phase === 'stream-failed')) {
    stage = { kind: 'stale' };
  }

  const known: Generation[] = [state.generation, state.lastGood, run?.prior ?? null].filter((g) => g !== null);
  const currentReplay = run?.replay ?? null;
  const activeChip = currentReplay?.chip ?? null;

  return (
    <div className="flex h-dvh flex-col">
      <header className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-3 max-sm:px-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fuchsia-400/80">AI UI Generator</p>
          <h1 className="text-lg font-semibold">Describe it, see it</h1>
        </div>
        <a
          href={CASE_STUDY_URL}
          className="rounded text-sm text-white/60 transition hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-fuchsia-400"
        >
          by Rokas<span className="hidden sm:inline"> · case study</span>{' '}
          <span aria-hidden="true">↗</span>
        </a>
      </header>

      <main className="flex min-h-0 flex-1 flex-col">
        <section aria-label="Prompt" className="border-b border-white/10 p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <label htmlFor="prompt" className="sr-only">
              Describe a component
            </label>
            <textarea
              id="prompt"
              value={state.prompt}
              onChange={(e) => dispatch({ type: 'setPrompt', prompt: e.target.value })}
              onFocus={prefetchSandbox}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate(state.prompt);
              }}
              placeholder="Describe a React + Tailwind component…"
              rows={2}
              maxLength={MAX_PROMPT_CHARS}
              className="flex-1 resize-none rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-base outline-none placeholder:text-white/50 focus:border-fuchsia-400/50 sm:text-sm"
            />
            <button
              type="button"
              onClick={() => generate(state.prompt)}
              onPointerEnter={prefetchSandbox}
              disabled={busy || !state.prompt.trim()}
              className="shrink-0 rounded-lg bg-fuchsia-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy && run?.replay === null ? 'Generating…' : 'Generate'}
            </button>
          </div>

          <div
            role="group"
            aria-label="Examples"
            className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 [mask-image:linear-gradient(to_right,#000_calc(100%_-_2.5rem),transparent)] [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:[mask-image:none]"
          >
            {EXAMPLES.map((ex) => {
              const recording = REPLAY_BY_CHIP.get(ex.id);
              return (
                <button
                  key={ex.id}
                  type="button"
                  onClick={() => {
                    dispatch({ type: 'setPrompt', prompt: ex.prompt });
                    if (recording === undefined) generate(ex.prompt);
                    else replay(recording);
                  }}
                  onPointerEnter={prefetchSandbox}
                  onFocus={prefetchSandbox}
                  disabled={busy}
                  aria-pressed={recording === undefined ? undefined : activeChip === ex.id}
                  className="relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/65 transition hover:border-fuchsia-400/40 hover:text-white disabled:opacity-40 aria-pressed:border-white/30 aria-pressed:bg-white/10 aria-pressed:text-white sm:py-1"
                >
                  <span className="sm:hidden">{ex.label}</span>
                  <span className="hidden sm:inline">{ex.prompt}</span>
                  {recording !== undefined && (
                    <span className="chip-tag border-l border-white/14 pl-2 font-mono text-[10px] font-medium tracking-[0.04em] text-white/42">
                      <span aria-hidden="true">$0</span>
                      <span className="sr-only">replays a recording, no request</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-baseline justify-between gap-4 text-xs text-white/50">
            <p>
              Prompts are sent to Anthropic’s Claude API; the preview runs on CodeSandbox’s bundler.{' '}
              <a
                href={PRIVACY_URL}
                className="text-white/65 underline decoration-white/25 underline-offset-2 transition hover:text-white"
              >
                Privacy
              </a>
            </p>
            <p className="hidden shrink-0 pointer-fine:block">
              <kbd className="rounded border border-white/15 bg-white/5 px-1 font-mono text-[11px] text-white/65">⌘</kbd>
              {' / '}
              <kbd className="rounded border border-white/15 bg-white/5 px-1 font-mono text-[11px] text-white/65">Ctrl</kbd>
              {' + '}
              <kbd className="rounded border border-white/15 bg-white/5 px-1 font-mono text-[11px] text-white/65">Enter</kbd>
              {' to generate'}
            </p>
          </div>

          {turnstileEnabled && (
            <TurnstileWidget
              onToken={setTurnstileToken}
              onError={(error) => dispatch({ type: 'blocked', error })}
            />
          )}

          {state.error !== null && !busy && (
            <p
              role="alert"
              className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm break-words text-red-300"
            >
              {state.error}
            </p>
          )}
        </section>

        <Rail run={run} />

        {workspace && (
          <div role="group" aria-label="Workspace view" className="flex border-b border-white/10 sm:hidden">
            {(['preview', 'code'] as const).map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={pane === p}
                aria-controls={p === 'preview' ? 'workspace-preview' : 'workspace-code'}
                onClick={() => setPane(p)}
                className="flex-1 py-2.5 text-sm text-white/55 transition focus-visible:-outline-offset-2 aria-pressed:text-white aria-pressed:shadow-[inset_0_-2px_0_var(--color-fuchsia-400)]"
              >
                {p === 'preview' ? 'Preview' : busy && streamedLines > 0 ? `Code · ${streamedLines} lines` : 'Code'}
              </button>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1">
          {workspace ? (
            <WorkspaceBoundary>
              <Suspense fallback={<WorkspaceLoading />}>
                <Sandbox
                  generation={state.generation}
                  known={known}
                  pane={pane}
                  code={code}
                  codeMeta={codeMeta}
                  stage={stage}
                  previewBar={
                    <PreviewBar
                      run={run}
                      busy={busy}
                      onReplay={currentReplay === null ? null : () => replay(currentReplay)}
                      onRunLive={currentReplay === null ? null : () => runPrompt(currentReplay.prompt)}
                    />
                  }
                  onShowCode={showCode}
                  onCompiled={onCompiled}
                  onCompileError={onCompileError}
                  onBundlerSilent={onBundlerSilent}
                  onBundlerRetry={onBundlerRetry}
                  onRegenerate={runPrompt}
                />
              </Suspense>
            </WorkspaceBoundary>
          ) : (
            <IdlePanel />
          )}
        </div>

        {scrubbable && run !== null && <Scrubber run={run} at={rewound} onScrub={onScrub} />}
      </main>
    </div>
  );
}

function PreviewBar({
  run,
  busy,
  onReplay,
  onRunLive,
}: {
  run: Run;
  busy: boolean;
  onReplay: (() => void) | null;
  onRunLive: (() => void) | null;
}) {
  const recording = run.replay;
  let pill: ReactNode;
  if (recording !== null) {
    pill = (
      <>
        <span className="flex min-w-0 items-center gap-[7px]">
          <span className="text-white/88">REPLAY</span>
          <span aria-hidden="true" className="text-white/25 max-sm:hidden">
            ·
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-[7px]">
          <span className="text-white/75">recorded {recordedDate(recording.recordedAt)}</span>
          <span aria-hidden="true" className="text-white/25">
            ·
          </span>
          <span className="truncate">{recording.model}</span>
        </span>
      </>
    );
  } else {
    pill = (
      <span className="flex min-w-0 items-center gap-[7px]">
        <span className="text-white/88">LIVE</span>
        <span aria-hidden="true" className="text-white/25">
          ·
        </span>
        <span className="truncate">{run.final?.model ?? MODEL}</span>
      </span>
    );
  }
  const actions = REPLAYS.length > 0 && onReplay !== null && onRunLive !== null;
  return (
    <div className="flex h-10 shrink-0 items-center justify-between gap-3 border-b border-white/[0.07] bg-[#0a0a0f] pr-3 pl-4 max-sm:h-auto max-sm:flex-wrap max-sm:gap-y-2 max-sm:px-3 max-sm:py-2">
      <p className="inline-flex min-w-0 items-center gap-[7px] overflow-hidden rounded-full border border-white/12 bg-white/[0.03] px-2.5 py-[3px] font-mono text-[11px] whitespace-nowrap text-white/50 max-sm:grid max-sm:w-full max-sm:gap-0 max-sm:rounded-lg max-sm:whitespace-normal">
        {pill}
      </p>
      {actions && (
        <div className="flex shrink-0 gap-2 max-sm:w-full max-sm:[&>*]:flex-1 max-sm:[&>*]:justify-center">
          <button type="button" onClick={onReplay} disabled={busy} className="btn-ghost">
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2.5 8a5.5 5.5 0 1 0 1.7-3.97" />
              <path d="M2.5 2.5v3h3" />
            </svg>
            <span>
              Replay build <span className="font-mono text-[11px] font-medium text-white/55">· $0</span>
            </span>
          </button>
          <button type="button" onClick={onRunLive} disabled={busy} className="btn-live">
            Run it live
          </button>
        </div>
      )}
    </div>
  );
}

const SKELETON: ReadonlyArray<readonly [indent: number, width: number]> = [
  [0, 58],
  [1, 22],
  [2, 70],
  [3, 46],
  [3, 62],
  [4, 38],
  [3, 54],
  [2, 12],
  [1, 8],
  [0, 4],
];

function IdlePanel() {
  return (
    <div data-idle className="flex h-full">
      <div aria-hidden="true" className="hidden w-2/5 flex-col gap-3 border-r border-white/10 bg-[#0e0e14] px-6 py-5 sm:flex">
        {SKELETON.map(([indent, width], i) => (
          <div key={i} className="flex items-center gap-4">
            <span className="w-4 text-right font-mono text-[11px] text-white/20">{i + 1}</span>
            <span
              className="h-2 rounded-full bg-white/[0.07]"
              style={{ marginLeft: `${indent * 1.25}rem`, width: `${width}%` }}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
        <div className="max-w-sm">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-fuchsia-400/80">Preview</p>
          <p className="mt-2 text-lg font-semibold">Your generated component renders here.</p>
          <ol className="mt-5 space-y-3 text-sm text-white/65">
            {[
              'Describe a component, or pick an example above.',
              'Claude writes it in React + Tailwind. The code streams in as it is written.',
              'The finished component renders live. Edit the code and the preview follows.',
            ].map((step, i) => (
              <li key={step} className="flex gap-3">
                <span className="pt-0.5 font-mono text-xs text-fuchsia-300/80">{String(i + 1).padStart(2, '0')}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function WorkspaceLoading() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <p role="status" className="font-mono text-sm text-white/55">
        Loading the preview…
      </p>
    </div>
  );
}
